import React, { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { today } from '../lib/dates.js';
import { parseNumber } from '../lib/numbers.js';
import { Alert, Card, CardSkeleton, Field, Pill, useAsync } from '../components/ui.jsx';
import UserSettings from '../components/UserSettings.jsx';

const CONTROL = [['off', 'Off'], ['warn', 'Warn only'], ['block', 'Hold for approval']];

/** Everything an entity is registered for, and how hard each control bites. */
export default function Settings() {
  const session = useSession();
  // A workspace with no entity yet would otherwise take the whole screen down.
  const entity = session.entities[0] || null;
  const entityId = entity ? entity.id : null;
  // The server keeps these to the workspace administrator (routes/statutory.js,
  // commercial.js, quality.js, alerts.js, admin-export.js). No Tally company is
  // set here: the Connector works with whichever company is selected in Tally.
  const isAdmin = session.user.role === 'tenant_admin';
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const statutory = useAsync(
    () => (entityId ? api.get(`/api/entities/${entityId}/statutory`) : Promise.resolve(null)), [entityId]);
  const entities = useAsync(() => api.get('/api/entities'), []);

  const downloadExport = async () => {
    setError(null);
    try {
      const res = await api.raw('/api/workspace/export?pretty=true');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `invit-export-${today()}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); }
  };

  const patch = async (path, body, label) => {
    setError(null); setSaved(null);
    try {
      await api.patch(path, body);
      setSaved(label);
      statutory.reload(); entities.reload();
    } catch (e) { setError(e.message); }
  };

  // A statutory figure saved on blur. Clearing the box used to save 0, and a
  // TCS threshold of 0 charges TCS on every sale; blank or unchanged saves nothing.
  const patchNumber = (field, label, ev, current) => {
    const n = parseNumber(ev.target.value);
    if (n === null && current !== null && current !== undefined) ev.target.value = current;
    if (n === null || (current !== null && current !== undefined && n === Number(current))) return;
    if (!Number.isFinite(n) || n < 0) { setSaved(null); setError(`${label} must be a number.`); return; }
    patch(`/api/entities/${entity.id}/statutory`, { [field]: n }, label);
  };

  if (!entity) {
    return <Alert kind="warn">This workspace has no entity yet. A platform administrator sets one up at onboarding.</Alert>;
  }
  if (statutory.loading && !statutory.data) return <CardSkeleton lines={5} />;
  if (statutory.error || !statutory.data) {
    return <Alert onRetry={statutory.reload}>{statutory.error || 'Could not load the settings for this entity.'}</Alert>;
  }
  const s = statutory.data;
  const e = entities.data && entities.data.entities.find((x) => x.id === entity.id);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-sub">{entity.name} · GSTIN {entity.gstin || 'not set'}</div>
        </div>
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      {saved && <Alert kind="ok">{saved} saved.</Alert>}

      {!isAdmin && (
        <Alert kind="info">
          Controls, statutory reporting, mail, alerts and the workspace export are changed by the workspace
          administrator.
        </Alert>
      )}

      {isAdmin && <>
      <UserSettings setError={setError} onSaved={setSaved} />

      <Card title="Commercial controls" note="How the application behaves when a rule is broken">
        <div className="row">
          <Field label="Rate below its floor">
            <select value={e ? e.rate_control : 'block'}
              onChange={(ev) => patch(`/api/entities/${entity.id}/controls`, { rate_control: ev.target.value }, 'Rate control')}>
              {CONTROL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Beyond the credit limit">
            <select value={e ? e.credit_control : 'warn'}
              onChange={(ev) => patch(`/api/entities/${entity.id}/controls`, { credit_control: ev.target.value }, 'Credit control')}>
              {CONTROL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Quality control">
        <div className="row">
          <Field label="Require a QC release before a batch can be sold">
            <select value={String(e ? e.require_qc_approval : false)}
              onChange={(ev) => patch(`/api/entities/${entity.id}/controls`, { require_qc_approval: ev.target.value === 'true' }, 'QC release')}>
              <option value="false">No — recorded properties are enough</option>
              <option value="true">Yes — somebody must release the batch</option>
            </select>
          </Field>
          <Field label="Buyer's own specification">
            <select value={e ? e.spec_match_control : 'warn'}
              onChange={(ev) => patch(`/api/entities/${entity.id}/controls`, { spec_match_control: ev.target.value }, 'Specification matching')}>
              {CONTROL.map(([v, l]) => <option key={v} value={v}>{l === 'Hold for approval' ? 'Refuse the despatch' : l}</option>)}
            </select>
          </Field>
        </div>
        <div className="card-note">
          A buyer's contracted specification can be tighter than the house one. Checked against the batches actually
          allocated, at the moment the goods would leave.
        </div>
      </Card>

      <FeatureSettings entityId={entity.id} setError={setError} onSaved={(label) => { setSaved(label); session.refresh(); }} />

      <Card title="Sending documents" note="With SMTP set up, queued mail goes out within a few minutes. Without it the queue still records what would have gone out.">
        <SmtpSettings setError={setError} onSaved={() => setSaved('Mail settings')} />
      </Card>

      <Card title="e-Invoice and e-Way Bill"
        note="Reported through the provider this entity is set up with. The credentials are stored encrypted and never shown again.">
        <div className="row">
          <Field label="e-Invoice (IRN)">
            <select value={String(s.einvoice_enabled)}
              onChange={(ev) => patch(`/api/entities/${entity.id}/statutory`, { einvoice_enabled: ev.target.value === 'true' }, 'e-Invoice')}>
              <option value="false">Off</option><option value="true">On</option>
            </select>
          </Field>
          <Field label="e-Way Bill">
            <select value={String(s.ewaybill_enabled)}
              onChange={(ev) => patch(`/api/entities/${entity.id}/statutory`, { ewaybill_enabled: ev.target.value === 'true' }, 'e-Way bill')}>
              <option value="false">Off</option><option value="true">On</option>
            </select>
          </Field>
          <Field label="Provider">
            <select value={s.irp_provider}
              onChange={(ev) => patch(`/api/entities/${entity.id}/statutory`, { irp_provider: ev.target.value }, 'Provider')}>
              <option value="mock">Built-in (testing)</option>
              <option value="gsp">GSP (production)</option>
            </select>
          </Field>
          <Field label="e-Way bill threshold">
            <input className="num" defaultValue={s.ewb_threshold}
              onBlur={(ev) => patchNumber('ewb_threshold', 'e-Way bill threshold', ev, s.ewb_threshold)} />
          </Field>
        </div>
        {s.irp_provider === 'mock' && (
          <Alert kind="warn">
            The built-in provider issues well-formed but fictitious numbers. It is for testing the flow — switch to a
            GSP before billing for real.
          </Alert>
        )}
        {s.irp_provider === 'gsp' && <GspCredentials entityId={entity.id} secrets={s.secrets} config={s.irp_config} onSaved={statutory.reload} setError={setError} />}
      </Card>

      <Card title="TCS under 206C(1H)" note="Collected on sales to a buyer beyond the yearly threshold">
        <div className="row">
          <Field label="Collect TCS">
            <select value={String(s.tcs_enabled)}
              onChange={(ev) => patch(`/api/entities/${entity.id}/statutory`, { tcs_enabled: ev.target.value === 'true' }, 'TCS')}>
              <option value="false">No</option><option value="true">Yes</option>
            </select>
          </Field>
          <Field label="Rate %">
            <input className="num" defaultValue={s.tcs_rate}
              onBlur={(ev) => patchNumber('tcs_rate', 'TCS rate', ev, s.tcs_rate)} />
          </Field>
          <Field label="Threshold per buyer, per year">
            <input className="num" defaultValue={s.tcs_threshold}
              onBlur={(ev) => patchNumber('tcs_threshold', 'TCS threshold', ev, s.tcs_threshold)} />
          </Field>
        </div>
        <div className="card-note">
          Only the part of an invoice that carries the buyer past the threshold attracts TCS, and it is settled again
          when the invoice is issued, because the year's running total may have moved.
        </div>
      </Card>

      <Card title="Exports under LUT">
        <div className="row">
          <Field label="LUT number">
            <input defaultValue={s.lut_number || ''}
              onBlur={(ev) => patch(`/api/entities/${entity.id}/statutory`, { lut_number: ev.target.value }, 'LUT')} />
          </Field>
          <Field label="LUT date">
            <input type="date" defaultValue={s.lut_date ? s.lut_date.slice(0, 10) : ''}
              onBlur={(ev) => ev.target.value && patch(`/api/entities/${entity.id}/statutory`, { lut_date: ev.target.value }, 'LUT date')} />
          </Field>
        </div>
      </Card>

      <Card title="Alerts" note="Where the application tells you something is quietly wrong.">
        <AlertSettings setError={setError} onSaved={() => setSaved('Alert settings')} />
      </Card>

      <Card title="Your data"
        note="Everything this workspace owns, as JSON — for your own backup, or to take with you.">
        <div className="inline">
          <button className="btn secondary" onClick={downloadExport}>Download the workspace</button>
          <span className="card-note">Credentials are excluded, not obscured: no password hashes, no tokens.</span>
        </div>
      </Card>
      </>}
    </>
  );
}

function GspCredentials({ entityId, secrets, config, onSaved, setError }) {
  const [baseUrl, setBaseUrl] = useState((config && config.base_url) || '');
  const [fields, setFields] = useState({ api_key: '', username: '', password: '', client_id: '', client_secret: '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const provided = Object.fromEntries(Object.entries(fields).filter(([, v]) => v));
      await api.patch(`/api/entities/${entityId}/statutory`, {
        irp_config: { ...(config || {}), base_url: baseUrl },
        ...(Object.keys(provided).length ? { irp_secrets: provided } : {}),
      });
      setFields({ api_key: '', username: '', password: '', client_id: '', client_secret: '' });
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 6 }}>
      <div className="row">
        <Field label="Provider base URL">
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://gsp.example/api" />
        </Field>
        {['api_key', 'username', 'password', 'client_id', 'client_secret'].map((key) => (
          <Field key={key} label={key.replace(/_/g, ' ')}
            hint={secrets.keys.includes(key) ? 'stored' : undefined}>
            <input type={key.includes('password') || key.includes('secret') || key.includes('key') ? 'password' : 'text'}
              value={fields[key]} placeholder={secrets.keys.includes(key) ? '•••••••• (leave blank to keep)' : ''}
              onChange={(e) => setFields({ ...fields, [key]: e.target.value })} />
          </Field>
        ))}
      </div>
      <button className="btn" disabled={busy} onClick={save}>Save provider settings</button>
    </div>
  );
}

function SmtpSettings({ setError, onSaved }) {
  const [config, setConfig] = useState({ host: '', port: 587, secure: false, from: '' });
  const [secrets, setSecrets] = useState({ user: '', pass: '' });
  const [replyTo, setReplyTo] = useState('');
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(false);

  // Only what is safe to show: host, port, sender, reply-to. The login is sealed
  // on the server and never comes back; "stored" is all the form says about it.
  const apply = (res) => {
    if (!res) return;
    const c = res.smtp_config || res.config || {};
    setConfig((prev) => ({
      host: c.host ?? prev.host, port: c.port ?? prev.port, secure: c.secure ?? prev.secure, from: c.from ?? prev.from,
    }));
    if (res.reply_to) setReplyTo(res.reply_to);
    setConfigured(Boolean(res.smtp_configured));
  };

  // Prefill from the server when it offers the stored settings; a deployment
  // without that endpoint simply starts from an empty form.
  useEffect(() => {
    let live = true;
    api.get('/api/settings/smtp').then((res) => live && apply(res)).catch(() => {});
    return () => { live = false; };
  }, []);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      // A blank field means "leave it as it is", never "erase the stored value".
      const sent = {
        ...(config.host.trim() ? { host: config.host.trim() } : {}),
        ...(String(config.port).trim() ? { port: Number(config.port) } : {}),
        secure: Boolean(config.secure),
        ...(config.from.trim() ? { from: config.from.trim() } : {}),
      };
      const res = await api.put('/api/settings/smtp', {
        config: sent,
        ...(secrets.user && secrets.pass ? { secrets } : {}),
        ...(replyTo.trim() ? { reply_to: replyTo.trim() } : {}),
      });
      apply(res);
      setSecrets({ user: '', pass: '' });
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="row">
        <Field label="SMTP host"><input value={config.host} placeholder="smtp.example.com"
          onChange={(e) => setConfig({ ...config, host: e.target.value })} /></Field>
        <Field label="Port"><input className="num" value={config.port}
          onChange={(e) => setConfig({ ...config, port: e.target.value })} /></Field>
        <Field label="From address"><input value={config.from} placeholder="billing@yourcompany.example"
          onChange={(e) => setConfig({ ...config, from: e.target.value })} /></Field>
        <Field label="Reply-to"><input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} /></Field>
        <Field label="Username" hint={configured ? 'stored' : undefined}><input value={secrets.user}
          placeholder={configured ? '(leave blank to keep)' : ''} autoComplete="off"
          onChange={(e) => setSecrets({ ...secrets, user: e.target.value })} /></Field>
        <Field label="Password" hint={configured ? 'stored' : undefined}><input type="password" value={secrets.pass}
          placeholder={configured ? '•••••••• (leave blank to keep)' : ''} autoComplete="new-password"
          onChange={(e) => setSecrets({ ...secrets, pass: e.target.value })} /></Field>
      </div>
      <button className="btn" disabled={busy} onClick={save}>Save mail settings</button>
    </>
  );
}

function AlertSettings({ setError, onSaved }) {
  const [emails, setEmails] = useState('');
  const [webhook, setWebhook] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.put('/api/alerts/settings', {
        ...(emails ? { alert_emails: emails.split(',').map((x) => x.trim()).filter(Boolean) } : {}),
        ...(webhook ? { alert_webhook: webhook } : {}),
      });
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="row">
        <Field label="Email these addresses" hint="comma separated">
          <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ops@yourcompany.example" />
        </Field>
        <Field label="Or post to a webhook" hint="Slack, Teams, or your own endpoint">
          <input value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://hooks.example/invit" />
        </Field>
      </div>
      <button className="btn" disabled={busy} onClick={save}>Save alert settings</button>
      <div className="card-note" style={{ marginTop: 10 }}>
        Raised when customers are overdue or over their limit, a document waits to be confirmed as entered in Tally,
        an agent stops reporting, a sync job gives up, an issued invoice has not reached Tally for an
        hour, stock cannot be billed, or a batch is about to expire.
      </div>
    </>
  );
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WA_DEFAULT = 'Dear {buyer},\nPlease find our {document} {number} dated {date} for {amount}:\n{link}\n\nRegards,\n{company}';

/** WhatsApp sharing, scanning at the godown, and how overdue customers are handled. */
function FeatureSettings({ entityId, setError, onSaved }) {
  const f = useAsync(() => api.get(`/api/entities/${entityId}/features`), [entityId]);
  const [message, setMessage] = useState(null);

  const save = async (patch, label) => {
    setError(null);
    try { await api.patch(`/api/entities/${entityId}/features`, patch); f.reload(); onSaved(label); }
    catch (e) { setError(e.message); }
  };
  if (!f.data) return f.loading ? <CardSkeleton lines={3} /> : null;
  const s = f.data;
  const text = message ?? (s.whatsapp_message || WA_DEFAULT);
  const bool = (v) => v === 'true';

  return (
    <>
      <Card title="WhatsApp" note="Opens WhatsApp on the user's phone or computer with a link to the document; nothing is sent without them pressing send.">
        <div className="row">
          <Field label="Share invoices and credit notes on WhatsApp">
            <select value={String(s.whatsapp_enabled)} onChange={(e) => save({ whatsapp_enabled: bool(e.target.value) }, 'WhatsApp sharing')}>
              <option value="false">Off</option><option value="true">On</option>
            </select>
          </Field>
          <Field label="Link works for" hint="days">
            <input className="num" defaultValue={s.whatsapp_link_days} key={`days-${s.whatsapp_link_days}`}
              onBlur={(e) => {
                const n = parseNumber(e.target.value);
                if (n === null || n === s.whatsapp_link_days) return;
                if (!Number.isInteger(n) || n < 1 || n > 365) { setError('The link must work for 1 to 365 days.'); return; }
                save({ whatsapp_link_days: n }, 'Link expiry');
              }} />
          </Field>
          <Field label="Add certificates of analysis">
            <select value={String(s.whatsapp_include_coa)} onChange={(e) => save({ whatsapp_include_coa: bool(e.target.value) }, 'Certificates on WhatsApp')}>
              <option value="false">No, the invoice only</option><option value="true">Yes, a link per released batch</option>
            </select>
          </Field>
        </div>
        <Field label="Message" hint="{buyer} {document} {number} {date} {amount} {link} {company}">
          <textarea rows={5} value={text} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        <div className="inline">
          <button className="btn secondary" disabled={message === null}
            onClick={() => { save({ whatsapp_message: text.trim() === WA_DEFAULT ? null : text }, 'WhatsApp message'); setMessage(null); }}>
            Save message
          </button>
          <button className="btn ghost" onClick={() => { setMessage(null); save({ whatsapp_message: null }, 'WhatsApp message'); }}>
            Back to the standard message
          </button>
        </div>
      </Card>

      <Card title="Godown scanning" note="Batch codes scanned with a phone camera or a barcode scanner as the goods are loaded.">
        <div className="row">
          <Field label="Scan batch codes at despatch">
            <select value={s.godown_scan_mode} onChange={(e) => save({ godown_scan_mode: e.target.value }, 'Godown scanning')}>
              <option value="off">Off</option>
              <option value="optional">Offered — scanning checks each drum against the despatch</option>
              <option value="required">Required — every batch must be scanned before confirming</option>
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Collections" note="Customers with bills overdue, checked when an order is raised and chased with reminders.">
        <div className="row">
          <Field label="Overdue bills when an order is raised">
            <select value={s.overdue_control} onChange={(e) => save({ overdue_control: e.target.value }, 'Overdue control')}>
              {CONTROL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Grace after the due date" hint="days">
            <input className="num" defaultValue={s.overdue_grace_days} key={`grace-${s.overdue_grace_days}`}
              onBlur={(e) => {
                const n = parseNumber(e.target.value);
                if (n === null || n === s.overdue_grace_days) return;
                if (!Number.isInteger(n) || n < 0 || n > 365) { setError('Grace days must be 0 to 365.'); return; }
                save({ overdue_grace_days: n }, 'Grace days');
              }} />
          </Field>
          <Field label="Alert on customers to chase">
            <select value={String(s.collections_alert)} onChange={(e) => save({ collections_alert: bool(e.target.value) }, 'Collections alert')}>
              <option value="true">Yes</option><option value="false">No</option>
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="E-mail payment reminders to customers">
            <select value={s.reminder_schedule} onChange={(e) => save({ reminder_schedule: e.target.value }, 'Reminder schedule')}>
              <option value="off">Only when somebody sends one</option>
              <option value="weekly">Every week, automatically</option>
            </select>
          </Field>
          {s.reminder_schedule === 'weekly' && (
            <Field label="On">
              <select value={s.reminder_weekday} onChange={(e) => save({ reminder_weekday: Number(e.target.value) }, 'Reminder day')}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="card-note">
          The weekly run mails every customer with an e-mail address and a bill overdue past the grace days. Mail goes
          out once SMTP is set up below. A reminder can also be sent by hand, on WhatsApp or by e-mail, from Receivables.
        </div>
      </Card>
    </>
  );
}
